import React, { useState, useEffect } from 'react';
import { RiskCard, RiskCardHeader, RiskCardBody } from '../risk/UiKit';

const API_BASE = process.env.REACT_APP_API_URL + '/api';

export default function MLAnalyticsDashboard() {
  const [analytics, setAnalytics] = useState({
    riskPredictions: [],
    capTimelines: [],
    memoClassifications: [],
    errorClassifications: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState('7d');
  const [selectedApp, setSelectedApp] = useState('all');

  useEffect(() => {
    fetchAnalytics();
  }, [timeRange, selectedApp]);

  const fetchAnalytics = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const [riskRes, capRes, memoRes, errorRes] = await Promise.all([
        fetch(`${API_BASE}/risk-assessment/ml/analytics?timeRange=${timeRange}`, {
          credentials: 'include'
        }),
        fetch(`${API_BASE}/analytics/ml/timeline-analytics?timeRange=${timeRange}`, {
          credentials: 'include'
        }),
        fetch(`${API_BASE}/memo-analysis/ml/analytics?timeRange=${timeRange}`, {
          credentials: 'include'
        }),
        fetch(`${API_BASE}/upload/ml/error-analytics?timeRange=${timeRange}`, {
          credentials: 'include'
        })
      ]);

      const [riskData, capData, memoData, errorData] = await Promise.all([
        riskRes.json(),
        capRes.json(),
        memoRes.json(),
        errorRes.json()
      ]);

      setAnalytics({
        riskPredictions: riskData.success ? riskData.predictions : [],
        capTimelines: capData.success ? capData.predictions : [],
        memoClassifications: memoData.success ? memoData.predictions : [],
        errorClassifications: errorData.success ? errorData.predictions : []
      });
    } catch (err) {
      setError('Failed to load analytics data');
      console.error('Analytics Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const getTimeRangeLabel = (range) => {
    switch (range) {
      case '1d': return 'Last 24 Hours';
      case '7d': return 'Last 7 Days';
      case '30d': return 'Last 30 Days';
      case '90d': return 'Last 90 Days';
      default: return 'All Time';
    }
  };

  const getAppLabel = (app) => {
    switch (app) {
      case 'risk': return 'Risk Assessment';
      case 'cap': return 'CAP Tool';
      case 'memo': return 'Mandates Tracker';
      case 'error': return 'Audit Scrubber';
      default: return 'All Applications';
    }
  };

  const calculateAccuracy = (predictions) => {
    if (!predictions || predictions.length === 0) return 0;
    const avgConfidence = predictions.reduce((sum, p) => sum + (p.confidence_level || 0), 0) / predictions.length;
    return Math.round(avgConfidence * 100);
  };

  const calculateVolume = (predictions) => {
    return predictions ? predictions.length : 0;
  };

  const getTopCategories = (predictions, field) => {
    if (!predictions || predictions.length === 0) return [];
    
    const counts = predictions.reduce((acc, p) => {
      const value = p[field];
      if (value) {
        acc[value] = (acc[value] || 0) + 1;
      }
      return acc;
    }, {});

    return Object.entries(counts)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 5)
      .map(([category, count]) => ({ category, count }));
  };

  const getRiskDistribution = () => {
    const riskScores = analytics.riskPredictions.map(p => p.predicted_risk_score || 0);
    const low = riskScores.filter(s => s < 40).length;
    const medium = riskScores.filter(s => s >= 40 && s < 70).length;
    const high = riskScores.filter(s => s >= 70).length;
    
    return { low, medium, high, total: riskScores.length };
  };

  const getCAPTimelineStats = () => {
    const timelines = analytics.capTimelines.map(p => p.predicted_days_to_close || 0);
    const avgDays = timelines.length > 0 ? Math.round(timelines.reduce((sum, d) => sum + d, 0) / timelines.length) : 0;
    const onTrack = timelines.filter(d => d <= 30).length;
    const atRisk = timelines.filter(d => d > 30 && d <= 60).length;
    const overdue = timelines.filter(d => d > 60).length;
    
    return { avgDays, onTrack, atRisk, overdue, total: timelines.length };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <div className="text-red-600 mb-4">{error}</div>
        <button 
          onClick={fetchAnalytics}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Retry
        </button>
      </div>
    );
  }

  const riskDist = getRiskDistribution();
  const capStats = getCAPTimelineStats();

  return (
    <div className="space-y-6">
      {/* Header Controls */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">ML Analytics Dashboard</h2>
          <p className="text-gray-600">Cross-platform machine learning insights and predictions</p>
        </div>
        
        <div className="flex items-center space-x-4">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="1d">Last 24 Hours</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
            <option value="90d">Last 90 Days</option>
            <option value="all">All Time</option>
          </select>
          
          <select
            value={selectedApp}
            onChange={(e) => setSelectedApp(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Applications</option>
            <option value="risk">Risk Assessment</option>
            <option value="cap">CAP Tool</option>
            <option value="memo">Mandates Tracker</option>
            <option value="error">Audit Scrubber</option>
          </select>
        </div>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <RiskCard>
          <RiskCardBody className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Risk Predictions</p>
                <p className="text-2xl font-bold text-gray-900">{calculateVolume(analytics.riskPredictions)}</p>
                <p className="text-xs text-gray-500">{calculateAccuracy(analytics.riskPredictions)}% avg confidence</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
            </div>
          </RiskCardBody>
        </RiskCard>

        <RiskCard>
          <RiskCardBody className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">CAP Timelines</p>
                <p className="text-2xl font-bold text-gray-900">{calculateVolume(analytics.capTimelines)}</p>
                <p className="text-xs text-gray-500">{capStats.avgDays} days avg</p>
              </div>
              <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </RiskCardBody>
        </RiskCard>

        <RiskCard>
          <RiskCardBody className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Memo Classifications</p>
                <p className="text-2xl font-bold text-gray-900">{calculateVolume(analytics.memoClassifications)}</p>
                <p className="text-xs text-gray-500">{calculateAccuracy(analytics.memoClassifications)}% avg confidence</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
            </div>
          </RiskCardBody>
        </RiskCard>

        <RiskCard>
          <RiskCardBody className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Error Classifications</p>
                <p className="text-2xl font-bold text-gray-900">{calculateVolume(analytics.errorClassifications)}</p>
                <p className="text-xs text-gray-500">{calculateAccuracy(analytics.errorClassifications)}% avg confidence</p>
              </div>
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
            </div>
          </RiskCardBody>
        </RiskCard>
      </div>

      {/* Risk Distribution */}
      {analytics.riskPredictions.length > 0 && (
        <RiskCard>
          <RiskCardHeader title="Risk Score Distribution" subtitle="Predicted risk levels across assessments" />
          <RiskCardBody>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <div className="text-2xl font-bold text-green-600">{riskDist.low}</div>
                <div className="text-sm text-green-700">Low Risk</div>
                <div className="text-xs text-green-600">({riskDist.total > 0 ? Math.round((riskDist.low / riskDist.total) * 100) : 0}%)</div>
              </div>
              <div className="text-center p-4 bg-yellow-50 rounded-lg">
                <div className="text-2xl font-bold text-yellow-600">{riskDist.medium}</div>
                <div className="text-sm text-yellow-700">Medium Risk</div>
                <div className="text-xs text-yellow-600">({riskDist.total > 0 ? Math.round((riskDist.medium / riskDist.total) * 100) : 0}%)</div>
              </div>
              <div className="text-center p-4 bg-red-50 rounded-lg">
                <div className="text-2xl font-bold text-red-600">{riskDist.high}</div>
                <div className="text-sm text-red-700">High Risk</div>
                <div className="text-xs text-red-600">({riskDist.total > 0 ? Math.round((riskDist.high / riskDist.total) * 100) : 0}%)</div>
              </div>
            </div>
          </RiskCardBody>
        </RiskCard>
      )}

      {/* CAP Timeline Status */}
      {analytics.capTimelines.length > 0 && (
        <RiskCard>
          <RiskCardHeader title="CAP Timeline Status" subtitle="Predicted completion timelines" />
          <RiskCardBody>
            <div className="grid grid-cols-4 gap-4">
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <div className="text-2xl font-bold text-green-600">{capStats.onTrack}</div>
                <div className="text-sm text-green-700">On Track</div>
                <div className="text-xs text-green-600">≤30 days</div>
              </div>
              <div className="text-center p-4 bg-yellow-50 rounded-lg">
                <div className="text-2xl font-bold text-yellow-600">{capStats.atRisk}</div>
                <div className="text-sm text-yellow-700">At Risk</div>
                <div className="text-xs text-yellow-600">31-60 days</div>
              </div>
              <div className="text-center p-4 bg-red-50 rounded-lg">
                <div className="text-2xl font-bold text-red-600">{capStats.overdue}</div>
                <div className="text-sm text-red-700">Overdue</div>
                <div className="text-xs text-red-600">>60 days</div>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <div className="text-2xl font-bold text-gray-600">{capStats.avgDays}</div>
                <div className="text-sm text-gray-700">Avg Days</div>
                <div className="text-xs text-gray-600">To Complete</div>
              </div>
            </div>
          </RiskCardBody>
        </RiskCard>
      )}

      {/* Top Categories */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {analytics.memoClassifications.length > 0 && (
          <RiskCard>
            <RiskCardHeader title="Top Memo Categories" subtitle="Most classified memo types" />
            <RiskCardBody>
              <div className="space-y-3">
                {getTopCategories(analytics.memoClassifications, 'predicted_category').map((item, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">{item.category}</span>
                    <span className="text-sm text-gray-600">{item.count}</span>
                  </div>
                ))}
              </div>
            </RiskCardBody>
          </RiskCard>
        )}

        {analytics.errorClassifications.length > 0 && (
          <RiskCard>
            <RiskCardHeader title="Top Error Types" subtitle="Most classified error types" />
            <RiskCardBody>
              <div className="space-y-3">
                {getTopCategories(analytics.errorClassifications, 'predictedErrorType').map((item, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">{item.category}</span>
                    <span className="text-sm text-gray-600">{item.count}</span>
                  </div>
                ))}
              </div>
            </RiskCardBody>
          </RiskCard>
        )}
      </div>

      {/* Model Performance */}
      <RiskCard>
        <RiskCardHeader title="Model Performance" subtitle="Accuracy and confidence metrics across all models" />
        <RiskCardBody>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{calculateAccuracy(analytics.riskPredictions)}%</div>
              <div className="text-sm text-gray-600">Risk Prediction</div>
              <div className="text-xs text-gray-500">Avg Confidence</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-amber-600">{calculateAccuracy(analytics.capTimelines)}%</div>
              <div className="text-sm text-gray-600">CAP Timeline</div>
              <div className="text-xs text-gray-500">Avg Confidence</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{calculateAccuracy(analytics.memoClassifications)}%</div>
              <div className="text-sm text-gray-600">Memo Classification</div>
              <div className="text-xs text-gray-500">Avg Confidence</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600">{calculateAccuracy(analytics.errorClassifications)}%</div>
              <div className="text-sm text-gray-600">Error Classification</div>
              <div className="text-xs text-gray-500">Avg Confidence</div>
            </div>
          </div>
        </RiskCardBody>
      </RiskCard>
    </div>
  );
}
